import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api/contexto'
import type { ErrorApi } from '../api/errores'
import type { components } from '../api/tipos'
import type { Filtros } from './useFiltrosUrl'

export type ClienteResumen = components['schemas']['ClienteResumen']
export type PaginaClientes = components['schemas']['Pagina_ClienteResumen_']

/**
 * 200 filas por pagina: el reparto de una semana entra casi siempre de una vez,
 * y el backend ya limita el tamano maximo.
 */
export const TAMANO_PAGINA = 200

export function useClientes(filtros: Filtros, tamanoPagina: number = TAMANO_PAGINA) {
  const api = useApi()

  return useQuery<PaginaClientes, ErrorApi>({
    // `filtros` entero en la clave: al cambiar de pagina o de filtro la clave
    // cambia y TanStack Query no puede servir el resultado anterior.
    queryKey: ['clientes', filtros, tamanoPagina],
    queryFn: ({ signal }) =>
      api.pedir<PaginaClientes>('/api/clientes', {
        parametros: {
          semana: filtros.semana,
          ruta: filtros.ruta,
          // El buscador se llama `q` en la API; el cliente omite el vacio.
          q: filtros.q,
          page: filtros.page,
          page_size: tamanoPagina,
        },
        senal: signal,
      }),
  })
}

/**
 * Baja / reactivación de un cliente (A.1.1). La baja puede ser indefinida
 * (sin `hasta`) o temporal (`hasta` = fecha ISO). Un cliente de baja queda
 * fuera de recálculo, envíos y agendamiento. Al terminar se refresca la lista.
 */
export function useBajaCliente() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation<unknown, ErrorApi, { contactId: string; hasta?: string }>({
    mutationFn: ({ contactId, hasta }) =>
      api.pedir(`/api/clientes/${contactId}/baja`, {
        metodo: 'POST',
        cuerpo: hasta ? { hasta } : {},
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['clientes'] }),
  })
}

export function useReactivarCliente() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation<unknown, ErrorApi, { contactId: string }>({
    mutationFn: ({ contactId }) =>
      api.pedir(`/api/clientes/${contactId}/reactivar`, { metodo: 'POST' }),
    onSuccess: (_d, { contactId }) => {
      void qc.invalidateQueries({ queryKey: ['clientes'] })
      void qc.invalidateQueries({ queryKey: ['perfil-cliente', contactId] })
    },
  })
}

/**
 * Interruptor de avisos (Iván oct-2026): con `avisar:false` el cliente deja de
 * recibir el aviso del lunes y sale del ciclo, sin darlo de baja.
 */
export function useFijarAvisar() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation<unknown, ErrorApi, { contactId: string; avisar: boolean }>({
    mutationFn: ({ contactId, avisar }) =>
      api.pedir(`/api/clientes/${contactId}/avisar`, { metodo: 'POST', cuerpo: { avisar } }),
    onSuccess: (_d, { contactId }) => {
      void qc.invalidateQueries({ queryKey: ['clientes'] })
      void qc.invalidateQueries({ queryKey: ['perfil-cliente', contactId] })
    },
  })
}

/**
 * Aplazar el próximo aviso N días (7/14/21/28). Mueve solo el aviso; NO toca
 * albaranes. Resuelve el baile de la S cuando ya se repartió al cliente.
 */
export function useAplazarDias() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation<unknown, ErrorApi, { contactId: string; dias: number }>({
    mutationFn: ({ contactId, dias }) =>
      api.pedir(`/api/clientes/${contactId}/aplazar`, { metodo: 'POST', cuerpo: { dias } }),
    onSuccess: (_d, { contactId }) => {
      void qc.invalidateQueries({ queryKey: ['clientes'] })
      void qc.invalidateQueries({ queryKey: ['perfil-cliente', contactId] })
    },
  })
}

/** Una nota de cliente (caché local de las notes de Holded). */
export interface NotaCliente {
  id: number
  contactId: string
  titulo: string | null
  texto: string
  autor: string | null
  creadaEn: string
  enHolded: boolean
}

/** Notas de un cliente (se escriben en Holded + caché del panel). */
export function useNotasCliente(contactId: string | null) {
  const api = useApi()
  return useQuery<{ items: NotaCliente[]; total: number }, ErrorApi>({
    queryKey: ['notas-cliente', contactId],
    enabled: !!contactId,
    queryFn: ({ signal }) =>
      api.pedir(`/api/clientes/${encodeURIComponent(contactId as string)}/notas`, { senal: signal }),
  })
}

export function useCrearNota(contactId: string | null) {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation<NotaCliente, ErrorApi, { texto: string; titulo?: string; autor?: string }>({
    mutationFn: (cuerpo) =>
      api.pedir(`/api/clientes/${encodeURIComponent(contactId as string)}/notas`, {
        metodo: 'POST',
        cuerpo,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['notas-cliente', contactId] }),
  })
}

export function useBorrarNota(contactId: string | null) {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation<unknown, ErrorApi, { notaId: number }>({
    mutationFn: ({ notaId }) =>
      api.pedir(`/api/clientes/${encodeURIComponent(contactId as string)}/notas/${notaId}`, {
        metodo: 'DELETE',
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['notas-cliente', contactId] }),
  })
}

/** Una dirección de entrega (multi-reparto) de un cliente. */
export interface DireccionEntrega {
  id: number
  contactId: string
  referencia: string | null
  destinatario: string | null
  direccion: string
  codigoPostal: string | null
  ciudad: string | null
  provincia: string | null
  activa: boolean
}

/** Datos para crear/editar una dirección de entrega. */
export interface DatosDireccionEntrega {
  referencia?: string | null
  destinatario?: string | null
  direccion: string
  codigoPostal?: string | null
  ciudad?: string | null
  provincia?: string | null
}

/** Direcciones de entrega activas de un cliente (multi-reparto). */
export function useDireccionesEntrega(contactId: string | null) {
  const api = useApi()
  return useQuery<DireccionEntrega[], ErrorApi>({
    queryKey: ['direcciones-entrega', contactId],
    enabled: !!contactId,
    queryFn: ({ signal }) =>
      api.pedir(`/api/clientes/${encodeURIComponent(contactId as string)}/direcciones-entrega`, { senal: signal }),
  })
}

export function useCrearDireccion(contactId: string | null) {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation<DireccionEntrega, ErrorApi, DatosDireccionEntrega>({
    mutationFn: (cuerpo) =>
      api.pedir(`/api/clientes/${encodeURIComponent(contactId as string)}/direcciones-entrega`, {
        metodo: 'POST',
        cuerpo,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['direcciones-entrega', contactId] }),
  })
}

export function useEditarDireccion(contactId: string | null) {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation<DireccionEntrega, ErrorApi, { dirId: number; datos: DatosDireccionEntrega }>({
    mutationFn: ({ dirId, datos }) =>
      api.pedir(`/api/clientes/${encodeURIComponent(contactId as string)}/direcciones-entrega/${dirId}`, {
        metodo: 'POST',
        cuerpo: datos,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['direcciones-entrega', contactId] }),
  })
}

export function useBorrarDireccion(contactId: string | null) {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation<unknown, ErrorApi, { dirId: number }>({
    mutationFn: ({ dirId }) =>
      api.pedir(`/api/clientes/${encodeURIComponent(contactId as string)}/direcciones-entrega/${dirId}`, {
        metodo: 'DELETE',
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['direcciones-entrega', contactId] }),
  })
}
